import { useState, useEffect } from 'react'
import { FileSpreadsheet, Database, Table, ArrowRight, Eye, Loader2 } from 'lucide-react'
import { coverageApi } from '@/api/client'

interface StatsResponse {
  countries: { count: number; last_imported: string | null }
  sources: { count: number; last_imported: string | null }
  recommendations: { count: number; last_imported: string | null }
}

// ─── Data Model ────────────────────────────────────────────────────────

interface SheetBox {
  id: string
  name: string
  description: string
  colour: string  // tailwind color class suffix (teal, blue, etc)
}

interface TableBox {
  id: string
  name: string
  description: string
  sources: string[]  // sheet IDs feeding this table
  columns: Array<{ name: string; type: string; from?: string; key?: boolean }>
  countKey: keyof StatsResponse
}

const SHEETS: SheetBox[] = [
  { id: 'summary', name: 'SUMMARY', description: 'Country-level capability flags (IDV, KYC 1+/2+/ID, KYB, AML)', colour: 'teal' },
  { id: 'kyc_summary', name: 'KYC Summary', description: 'Per-country metrics: coverage %, avg match rate, response time', colour: 'teal' },
  { id: 'kyc', name: 'KYC', description: 'Detailed data sources — vendor, PII flags, consent, mandatory/verifiable elements', colour: 'teal' },
  { id: 'kyc_rec', name: 'KYC Recommendations', description: 'Recommended vendor per country, API status, vendor notes', colour: 'teal' },
]

const TABLES: TableBox[] = [
  {
    id: 'country_coverage',
    name: 'country_coverage',
    description: 'One row per country — merged from SUMMARY + KYC Summary',
    sources: ['summary', 'kyc_summary'],
    countKey: 'countries',
    columns: [
      { name: 'country_name', type: 'TEXT', key: true, from: 'both' },
      { name: 'country_code', type: 'TEXT', from: 'derived' },
      { name: 'region', type: 'TEXT', from: 'kyc_summary' },
      { name: 'idv', type: 'INT', from: 'summary' },
      { name: 'kyc_one_plus', type: 'INT', from: 'summary' },
      { name: 'kyc_two_plus', type: 'INT', from: 'summary' },
      { name: 'kyc_gov_id', type: 'INT', from: 'summary' },
      { name: 'kyb_profile', type: 'INT', from: 'summary' },
      { name: 'kyb_ownership', type: 'INT', from: 'summary' },
      { name: 'aml', type: 'INT', from: 'summary' },
      { name: 'verification_type', type: 'TEXT', from: 'kyc_summary' },
      { name: 'data_source_count', type: 'INT', from: 'kyc_summary' },
      { name: 'coverage_estimate', type: 'REAL', from: 'kyc_summary' },
      { name: 'avg_match_rate', type: 'REAL', from: 'kyc_summary' },
      { name: 'avg_response_time', type: 'TEXT', from: 'kyc_summary' },
    ],
  },
  {
    id: 'kyc_data_sources',
    name: 'kyc_data_sources',
    description: 'One row per data source per country — the detailed PII matrix',
    sources: ['kyc'],
    countKey: 'sources',
    columns: [
      { name: 'country', type: 'TEXT', key: true },
      { name: 'country_code', type: 'TEXT', from: 'derived' },
      { name: 'vendor', type: 'TEXT' },
      { name: 'data_source_header', type: 'TEXT' },
      { name: 'purpose', type: 'TEXT' },
      { name: 'description', type: 'TEXT' },
      { name: 'has_first_name', type: 'INT' },
      { name: 'has_middle_name', type: 'INT' },
      { name: 'has_last_name', type: 'INT' },
      { name: 'has_dob', type: 'INT' },
      { name: 'has_gender', type: 'INT' },
      { name: 'has_address', type: 'INT' },
      { name: 'has_national_id', type: 'INT' },
      { name: 'has_phone', type: 'INT' },
      { name: 'government_id', type: 'INT' },
      { name: 'consent_required', type: 'INT' },
      { name: 'mandatory_pii', type: 'TEXT' },
      { name: 'verifiable_elements', type: 'TEXT' },
      { name: 'optional_elements', type: 'TEXT' },
      { name: 'adult_coverage', type: 'REAL' },
      { name: 'api_version', type: 'TEXT' },
      { name: 'national_id_name', type: 'TEXT' },
      { name: 'national_id_format', type: 'TEXT' },
    ],
  },
  {
    id: 'kyc_recommendations',
    name: 'kyc_recommendations',
    description: 'Recommended vendor per country with API status and notes',
    sources: ['kyc_rec'],
    countKey: 'recommendations',
    columns: [
      { name: 'country', type: 'TEXT', key: true },
      { name: 'country_code', type: 'TEXT', from: 'derived' },
      { name: 'iso_alpha3', type: 'TEXT' },
      { name: 'available_vendors', type: 'TEXT' },
      { name: 'recommended_vendor', type: 'TEXT' },
      { name: 'is_recommended_default', type: 'INT' },
      { name: 'api_status', type: 'TEXT' },
      { name: 'gov_id_available', type: 'INT' },
      { name: 'notes', type: 'TEXT' },
    ],
  },
]

function formatDate(d: string | null): string {
  if (!d) return 'Never'
  return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

// ─── Sub-components ────────────────────────────────────────────────────

function SheetCard({ sheet }: { sheet: SheetBox }) {
  return (
    <div className="p-3 rounded-lg border border-teal-500/30 bg-teal-500/5">
      <div className="flex items-start gap-2">
        <FileSpreadsheet size={14} className="text-teal-400 mt-0.5 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-xs font-semibold text-[var(--text-primary)]">{sheet.name}</p>
          <p className="text-[10px] text-[var(--text-muted)] mt-0.5 leading-tight">{sheet.description}</p>
        </div>
      </div>
    </div>
  )
}

function TableCard({ table, stats }: { table: TableBox; stats: StatsResponse | null }) {
  const [expanded, setExpanded] = useState(false)
  const stat = stats?.[table.countKey]

  const sheetSources = table.sources.map(id => SHEETS.find(s => s.id === id)?.name).filter(Boolean).join(', ')

  return (
    <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-3 text-left hover:bg-[var(--bg-tertiary)] transition-colors"
      >
        <div className="flex items-center gap-2 mb-1">
          <Table size={14} className="text-blue-400 flex-shrink-0" />
          <code className="text-xs font-mono font-semibold text-[var(--text-primary)]">{table.name}</code>
          <span className="ml-auto text-xs font-bold text-blue-400">
            {stat ? stat.count.toLocaleString() : '—'}
          </span>
        </div>
        <p className="text-[10px] text-[var(--text-muted)] ml-5">{table.description}</p>
        <p className="text-[10px] text-[var(--text-muted)] ml-5 mt-1">
          <span className="font-semibold">From:</span> {sheetSources}
          {stat?.last_imported && (
            <> • <span className="font-semibold">Last import:</span> {formatDate(stat.last_imported)}</>
          )}
        </p>
      </button>
      {expanded && (
        <div className="border-t border-[var(--border-color)] p-3 bg-[var(--bg-primary)]">
          <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase mb-2">Columns ({table.columns.length})</p>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1">
            {table.columns.map(col => (
              <div key={col.name} className="flex items-center gap-2 text-[10px]">
                {col.key && <span className="text-amber-400">🔑</span>}
                <code className="font-mono text-[var(--text-primary)]">{col.name}</code>
                <span className="text-[var(--text-muted)]">{col.type}</span>
                {col.from && col.from !== 'both' && col.from !== 'derived' && (
                  <span className="ml-auto text-[9px] text-teal-400">← {SHEETS.find(s => s.id === col.from)?.name || col.from}</span>
                )}
                {col.from === 'derived' && (
                  <span className="ml-auto text-[9px] text-amber-400">← derived</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function ViewCard({ stats }: { stats: StatsResponse | null }) {
  const [expanded, setExpanded] = useState(false)
  return (
    <div className="rounded-lg border border-violet-500/30 bg-violet-500/5 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-3 text-left hover:bg-violet-500/10 transition-colors"
      >
        <div className="flex items-center gap-2 mb-1">
          <Eye size={14} className="text-violet-400 flex-shrink-0" />
          <code className="text-xs font-mono font-semibold text-[var(--text-primary)]">v_kyc_country</code>
          <span className="ml-auto text-[10px] text-violet-400 font-semibold">SQL VIEW</span>
        </div>
        <p className="text-[10px] text-[var(--text-muted)] ml-5">
          Joins country_coverage + kyc_recommendations on country_code. Used by Match Rate Enquiry AI for efficient per-country lookups.
        </p>
        <p className="text-[10px] text-[var(--text-muted)] ml-5 mt-1">
          <span className="font-semibold">Rows:</span> {stats?.countries?.count?.toLocaleString() ?? '—'} (one per country)
        </p>
      </button>
      {expanded && (
        <div className="border-t border-violet-500/30 p-3 bg-[var(--bg-primary)]">
          <pre className="text-[10px] text-[var(--text-muted)] overflow-x-auto">{`CREATE VIEW v_kyc_country AS
SELECT cc.*, kr.recommended_vendor,
       kr.available_vendors, kr.api_status,
       kr.gov_id_available, kr.notes AS rec_notes,
       kr.is_recommended_default
FROM country_coverage cc
LEFT JOIN kyc_recommendations kr
  ON cc.country_code = kr.country_code`}</pre>
        </div>
      )}
    </div>
  )
}

// ─── Main Component ────────────────────────────────────────────────────

export function DatabaseVisualization() {
  const [stats, setStats] = useState<StatsResponse | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    coverageApi.stats().then(res => {
      if (res.success && res.data) setStats(res.data as StatsResponse)
      setLoading(false)
    })
  }, [])

  return (
    <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-5">
      <div className="flex items-center gap-2 mb-1">
        <Database size={16} className="text-blue-400" />
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">Database Structure</h3>
        {loading && <Loader2 size={12} className="animate-spin text-[var(--text-muted)] ml-2" />}
      </div>
      <p className="text-xs text-[var(--text-muted)] mb-5">
        How the spreadsheet sheets are transformed into database tables. Click tables to see their columns.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1.5fr] gap-4 items-start">
        {/* Sheets (source) */}
        <div className="space-y-2">
          <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase mb-2">XLSX Sheets (source)</p>
          {SHEETS.map(s => <SheetCard key={s.id} sheet={s} />)}
        </div>

        {/* Arrow divider */}
        <div className="hidden lg:flex items-center justify-center h-full pt-8">
          <ArrowRight size={20} className="text-[var(--text-muted)]" />
        </div>

        {/* Tables (destination) */}
        <div className="space-y-3">
          <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase mb-2">Database Tables</p>
          {TABLES.map(t => <TableCard key={t.id} table={t} stats={stats} />)}

          <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase mt-4 mb-2">Unified View (for AI)</p>
          <ViewCard stats={stats} />
        </div>
      </div>

      <div className="mt-5 p-3 rounded-lg bg-[var(--bg-tertiary)] text-xs text-[var(--text-muted)]">
        <p className="font-semibold text-[var(--text-secondary)] mb-1">How it flows</p>
        <ol className="list-decimal list-inside space-y-0.5">
          <li>Drag the xlsx into the import modal — parser reads each sheet</li>
          <li>SUMMARY + KYC Summary are merged by country into <code className="text-[var(--text-primary)]">country_coverage</code></li>
          <li>KYC sheet rows become <code className="text-[var(--text-primary)]">kyc_data_sources</code> (one per source per country)</li>
          <li>KYC Recommendations rows become <code className="text-[var(--text-primary)]">kyc_recommendations</code></li>
          <li>The <code className="text-[var(--text-primary)]">v_kyc_country</code> view joins coverage + recommendations so the Match Rate AI can query with one SQL call</li>
        </ol>
      </div>
    </div>
  )
}
